/**
 * JurisdictionReportSections — SystemAdmin per-state report "template". Sets the
 * visibility mode (Visible / Hidden / Null-Visible) for each report section, plus
 * field-by-field within the data-table sections (Vehicle, Title, Registration).
 *   • Visible      — show only when the state returns data (empties suppressed)
 *   • Hidden       — never show
 *   • Null-Visible — always show, even when empty ("No State Data")
 * Manages its own load/save, independent of the main jurisdiction form.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FileText, Loader2, Save, RefreshCw, CheckCircle } from 'lucide-react';
import { getErrorMessage } from '../../services/apiService';
import {
  getReportSections, saveReportSections, ReportSectionVisibility,
  getReportFields, saveReportFields, ReportFieldVisibility,
  ReportVisibility,
} from '../../services/jurisdictionsAdminService';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-black/[0.08] p-6 shadow-sm">
    <h2 className="text-sm font-semibold uppercase tracking-wider text-[#475569] mb-4">{title}</h2>
    {children}
  </div>
);

const MODE_OPTIONS = [
  { value: ReportVisibility.Visible, label: 'Visible' },
  { value: ReportVisibility.Hidden, label: 'Hidden' },
  { value: ReportVisibility.NullVisible, label: 'Always Show' },
];

const ModeSelect: React.FC<{ value: ReportVisibility; onChange: (v: ReportVisibility) => void; small?: boolean }> = ({ value, onChange, small }) => {
  const color =
    value === ReportVisibility.Hidden ? 'text-red-700 border-red-200 bg-red-50'
    : value === ReportVisibility.NullVisible ? 'text-amber-700 border-amber-200 bg-amber-50'
    : 'text-green-700 border-green-200 bg-green-50';
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value) as ReportVisibility)}
      className={`rounded border ${color} ${small ? 'text-xs px-1.5 py-0.5' : 'text-xs px-2 py-1'} focus:outline-none`}
    >
      {MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
};

const JurisdictionReportSections: React.FC<{ code: string }> = ({ code }) => {
  const [sections, setSections] = useState<ReportSectionVisibility[]>([]);
  const [fields, setFields] = useState<ReportFieldVisibility[]>([]);
  const [sectionMode, setSectionMode] = useState<Record<string, ReportVisibility>>({});
  const [fieldMode, setFieldMode] = useState<Record<string, ReportVisibility>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [s, f] = await Promise.all([getReportSections(code), getReportFields(code)]);
      setSections(s);
      setFields(f);
      setSectionMode(Object.fromEntries(s.map((r) => [r.key, r.visibility])));
      setFieldMode(Object.fromEntries(f.map((r) => [r.key, r.visibility])));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => { load(); }, [load]);

  const fieldsBySection = useMemo(() => {
    const m: Record<string, ReportFieldVisibility[]> = {};
    for (const f of fields) (m[f.sectionKey] ||= []).push(f);
    return m;
  }, [fields]);

  const dirty = useMemo(
    () =>
      sections.some((r) => sectionMode[r.key] !== r.visibility) ||
      fields.some((r) => fieldMode[r.key] !== r.visibility),
    [sections, fields, sectionMode, fieldMode],
  );

  const customCount = useMemo(
    () =>
      sections.filter((r) => (sectionMode[r.key] ?? ReportVisibility.Visible) !== ReportVisibility.Visible).length +
      fields.filter((r) => (fieldMode[r.key] ?? ReportVisibility.Visible) !== ReportVisibility.Visible).length,
    [sections, fields, sectionMode, fieldMode],
  );

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const [s, f] = await Promise.all([
        saveReportSections(code, sections.map((r) => ({ key: r.key, visibility: sectionMode[r.key] ?? ReportVisibility.Visible }))),
        saveReportFields(code, fields.map((r) => ({ key: r.key, visibility: fieldMode[r.key] ?? ReportVisibility.Visible }))),
      ]);
      setSections(s);
      setFields(f);
      setSectionMode(Object.fromEntries(s.map((r) => [r.key, r.visibility])));
      setFieldMode(Object.fromEntries(f.map((r) => [r.key, r.visibility])));
      setSuccess('Report template saved.');
      setTimeout(() => setSuccess(''), 2500);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Report Template (Sections & Fields)">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-[#64748b] leading-relaxed max-w-2xl">
          <FileText className="w-3.5 h-3.5 inline mr-1 text-[#c41e3a]" />
          The mode for each section/field on {code}'s report. {customCount > 0 && <span className="font-medium text-[#0f172a]">{customCount} customized.</span>}
        </p>
        <button onClick={load} className="text-xs text-[#64748b] hover:text-[#c41e3a] inline-flex items-center gap-1 shrink-0">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4 text-xs">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> <strong>Visible</strong> — always shown, unless the state doesn't provide it</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> <strong>Hidden</strong> — never shown</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> <strong>Always Show</strong> — shown even when null, empty, or not present (prints "No State Data")</span>
      </div>

      {error && <div className="mb-3 p-3 rounded-lg border bg-red-50 border-red-200 text-sm text-red-800">{error}</div>}
      {success && <div className="mb-3 p-3 rounded-lg border bg-green-50 border-green-200 text-sm text-green-800 inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4" /> {success}</div>}

      {loading ? (
        <div className="py-8 text-center text-[#64748b]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading template…</div>
      ) : (
        <div className="space-y-2">
          {sections.map((s) => {
            const sectionFields = fieldsBySection[s.key] ?? [];
            const sMode = sectionMode[s.key] ?? ReportVisibility.Visible;
            return (
              <div key={s.key} className="border-b border-black/[0.04] py-2">
                <div className="flex items-start justify-between gap-3">
                  <span>
                    <span className="block text-sm font-semibold text-[#0f172a]">{s.label}</span>
                    <span className="block text-xs text-[#94a3b8]">{s.description}</span>
                  </span>
                  <ModeSelect value={sMode} onChange={(v) => setSectionMode((p) => ({ ...p, [s.key]: v }))} />
                </div>

                {sectionFields.length > 0 && (
                  <div className={`mt-2 ml-3 pl-4 border-l border-black/[0.08] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1.5 ${sMode === ReportVisibility.Hidden ? 'opacity-40' : ''}`}>
                    {sectionFields.map((f) => (
                      <div key={f.key} className="flex items-center justify-between gap-2">
                        <span className="text-xs text-[#475569]">{f.label}</span>
                        <ModeSelect small value={fieldMode[f.key] ?? ReportVisibility.Visible} onChange={(v) => setFieldMode((p) => ({ ...p, [f.key]: v }))} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end mt-4">
        <button onClick={handleSave} disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-[#c41e3a] text-white hover:bg-[#a51830] disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save template
        </button>
      </div>
    </Section>
  );
};

export default JurisdictionReportSections;
